import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  adminStats, adminListStores, adminExtendTrial, adminMarkPaid,
  adminSuspend, adminResume, adminDeleteStore, clearAdminToken, getAdminToken, formatMXN,
  adminListPayments, adminConfirmPayment, adminRejectPayment,
  adminListNotifications, adminMarkNotificationRead, adminAdvisorsReport, adminCommissionsReport,
} from "@/lib/api";
import { toast, Toaster } from "sonner";
import { FxRateCard } from "@/components/admin/FxRateCard";
import {
  ShieldCheck, LogOut, Store as StoreIcon, Search, Clock, CheckCircle2,
  XCircle, Pause, Play, Trash2, Plus, DollarSign, Users, Package, Activity,
  Bell, Building2, Check, X, UserCheck, ChevronDown, ChevronRight,
  Link as LinkIcon, Copy, Wallet,
} from "lucide-react";

const STATUS_META = {
  pending_payment: { label: "Sin pagar", color: "text-[#ccff00]", bg: "bg-[#ccff00]/10 border-[#ccff00]/30" },
  trial:     { label: "Sin pagar",   color: "text-[#ccff00]", bg: "bg-[#ccff00]/10 border-[#ccff00]/30" },
  active:    { label: "Activa",   color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  expired:   { label: "Vencida",  color: "text-[#ff5a00]", bg: "bg-[#ff5a00]/10 border-[#ff5a00]/40" },
  suspended: { label: "Suspendida", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [advisorsReport, setAdvisorsReport] = useState(null);
  const [expandedAdvisor, setExpandedAdvisor] = useState(null);
  const [commissionsMonth, setCommissionsMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [commissionsReport, setCommissionsReport] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login"); return; }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, pays, notifs, advisors] = await Promise.all([
        adminStats(),
        adminListStores(statusFilter ? { status: statusFilter } : {}),
        adminListPayments("pending"),
        adminListNotifications(false),
        adminAdvisorsReport(),
      ]);
      setStats(s); setStores(r.stores);
      setPayments(pays.payments || []);
      setNotifications(notifs.notifications || []);
      setUnread(notifs.unread || 0);
      setAdvisorsReport(advisors);
      // Commissions loaded separately to allow month switching without full reload
      adminCommissionsReport(commissionsMonth).then(setCommissionsReport).catch(() => setCommissionsReport(null));
    } catch (e) {
      if (e?.response?.status === 401) { clearAdminToken(); nav("/admin/login"); return; }
      toast.error("No se cargaron los datos");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (getAdminToken()) load(); }, [statusFilter]);

  // Reload commissions independently when the month picker changes
  useEffect(() => {
    if (!getAdminToken()) return;
    adminCommissionsReport(commissionsMonth).then(setCommissionsReport).catch(() => setCommissionsReport(null));
  }, [commissionsMonth]);

  const inviteLink = (advisor) => {
    const base = window.location.origin;
    return `${base}/setup?advisor=${encodeURIComponent(advisor)}`;
  };
  const copyInvite = async (advisor) => {
    try {
      await navigator.clipboard.writeText(inviteLink(advisor));
      toast.success(`Link de ${advisor} copiado`);
    } catch {
      toast.error("No se pudo copiar. Copia manual: " + inviteLink(advisor));
    }
  };

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return stores;
    return stores.filter((s) =>
      (s.name || "").toLowerCase().includes(qq) ||
      (s.owner_name || "").toLowerCase().includes(qq) ||
      (s.phone || "").includes(qq) ||
      (s.id || "").includes(qq)
    );
  }, [q, stores]);

  const doAction = async (id, fn, msg) => {
    setBusyId(id);
    try { await fn(id); toast.success(msg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusyId(null); }
  };

  const doPayAction = async (id, fn, msg) => {
    setBusyId(id);
    try { await fn(id); toast.success(msg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusyId(null); }
  };

  const logout = () => { clearAdminToken(); toast("Sesión cerrada"); nav("/admin/login"); };

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <div className="grain-overlay" />

      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#ff5a00]" />
            <span className="font-display text-lg tracking-tighter">CHALANN</span>
            <span className="hidden md:inline text-white/30 mx-1">/</span>
            <span className="hidden md:inline chip"><ShieldCheck size={12} className="text-[#ccff00]" /> Centro de control</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/catalogo" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#ccff00]/30 text-[#ccff00] hover:bg-[#ccff00]/10 text-[10px] font-mono uppercase tracking-widest" data-testid="admin-catalogo-link">
              <Package size={12} /> Catálogo maestro
            </Link>
            <a href="#pagos" className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:text-white text-[10px] font-mono uppercase tracking-widest" data-testid="admin-bell">
              <Bell size={12} />
              Notificaciones
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#ff5a00] text-black text-[10px] font-bold flex items-center justify-center px-1" data-testid="admin-unread">{unread}</span>
              )}
            </a>
            <button onClick={logout} className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white" data-testid="admin-logout">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Plataforma</p>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-8">
            Negocios <span className="text-[#ff5a00]">afiliados</span>.
          </h1>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatCard icon={<StoreIcon size={16} />} label="Tiendas totales" value={stats?.total_stores ?? "—"} tint="text-white" tid="stat-total" />
          <StatCard icon={<Clock size={16} />} label="Sin pagar" value={stats?.pending_payment ?? stats?.trial ?? "—"} tint="text-[#ccff00]" tid="stat-trial" />
          <StatCard icon={<CheckCircle2 size={16} />} label="Activas (pagadas)" value={stats?.active ?? "—"} tint="text-emerald-400" tid="stat-active" />
          <StatCard icon={<XCircle size={16} />} label="Vencidas" value={stats?.expired ?? "—"} tint="text-[#ff5a00]" tid="stat-expired" />
          <StatCard icon={<DollarSign size={16} />} label="MRR estimado (USD)" value={`$${(stats?.mrr_usd ?? 0).toFixed(0)}`} sub={`${stats?.chalanito_active ?? 0} Chalanito · ${stats?.onepay_active ?? 0} One Pay · ≈ ${formatMXN(stats?.mrr_mxn || 0)}`} tint="text-emerald-400" tid="stat-mrr" />
          <StatCard icon={<Activity size={16} />} label="Ventas registradas" value={stats?.total_sales ?? "—"} tint="text-white" tid="stat-sales" />
          <StatCard icon={<Users size={16} />} label="Usuarios (staff+dueños)" value={stats?.total_users ?? "—"} tint="text-white" tid="stat-users" />
          <StatCard icon={<Package size={16} />} label="Productos en total" value={stats?.total_products ?? "—"} tint="text-white" tid="stat-products" />
        </div>

        {/* Pagos reportados por confirmar */}
        <section id="pagos" className="mb-10" data-testid="admin-payments">
          <div className="flex items-baseline justify-between mb-3">
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase">/ Pagos por confirmar</p>
            {payments.length > 0 && (
              <span className="font-display text-sm tracking-tight text-[#ccff00]">{payments.length} pendiente{payments.length !== 1 ? "s" : ""}</span>
            )}
          </div>
          {payments.length === 0 ? (
            <div className="border border-white/10 rounded-2xl p-6 text-center text-white/40 text-sm" data-testid="admin-payments-empty">
              Sin pagos pendientes por conciliar.
            </div>
          ) : (
            <div className="border border-white/10 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-[minmax(180px,2fr)_minmax(140px,1fr)_120px_120px_150px_170px] px-4 py-3 bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50">
                <span>Tienda</span><span>Depositó</span><span>Monto</span><span>Fecha</span><span>Referencia</span><span className="text-right pr-1">Acciones</span>
              </div>
              {payments.map((p) => (
                <div key={p.id} className="grid grid-cols-[minmax(180px,2fr)_minmax(140px,1fr)_120px_120px_150px_170px] items-center px-4 py-3 border-t border-white/5" data-testid={`admin-payment-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-display text-base tracking-tight truncate flex items-center gap-1"><Building2 size={12} className="text-white/40" /> {p.store_name}</p>
                    <p className="font-mono text-[10px] text-white/40 truncate">{p.store_id.slice(0, 8)}</p>
                  </div>
                  <div className="text-sm truncate">{p.depositor_name}</div>
                  <div className="font-display text-lg tracking-tighter text-[#ccff00]">{p.amount_mxn ? formatMXN(p.amount_mxn) : `$${p.amount_usd} USD`}<span className="ml-1 font-mono text-[9px] text-white/40 uppercase">{(p.plan || "chalanito") === "onepay" ? "One Pay" : "Chalanito"}{p.method === "stripe" ? " · tarjeta" : ""}</span></div>
                  <div className="text-sm">{fmtDate(p.paid_at)}</div>
                  <div className="text-sm truncate" title={p.reference}>{p.reference}</div>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      disabled={busyId === p.id}
                      onClick={() => doPayAction(p.id, adminConfirmPayment, "Pago confirmado · servicio activado")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-black text-[11px] font-mono uppercase tracking-wider hover:bg-emerald-400 disabled:opacity-50"
                      data-testid={`admin-confirm-${p.id}`}
                    >
                      <Check size={12} /> Confirmar
                    </button>
                    <button
                      disabled={busyId === p.id}
                      onClick={() => { if (window.confirm("¿Rechazar este pago?")) doPayAction(p.id, adminRejectPayment, "Pago rechazado"); }}
                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-[11px] font-mono uppercase tracking-wider disabled:opacity-50"
                      data-testid={`admin-reject-${p.id}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Notificaciones */}
        {notifications.length > 0 && (
          <section className="mb-10" data-testid="admin-notifications">
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-3">/ Notificaciones recientes</p>
            <div className="border border-white/10 rounded-2xl divide-y divide-white/5">
              {notifications.slice(0, 8).map((n) => (
                <div key={n.id} className={`px-4 py-3 flex items-start gap-3 ${!n.read ? "bg-[#ccff00]/[0.03]" : ""}`} data-testid={`admin-notif-${n.id}`}>
                  <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${n.read ? "bg-white/20" : "bg-[#ccff00]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm tracking-tight">{n.title}</p>
                    <p className="text-white/60 text-xs mt-0.5">{n.body}</p>
                    <p className="text-white/30 font-mono text-[10px] mt-1">{fmtDate(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <button onClick={() => adminMarkNotificationRead(n.id).then(load)} className="text-white/40 hover:text-white text-[10px] font-mono uppercase tracking-widest">
                      Marcar leída
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reporte de Asesores Comerciales */}
        <section id="asesores" className="mb-10" data-testid="admin-advisors">
          <div className="flex items-baseline justify-between mb-3">
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase flex items-center gap-2">
              <UserCheck size={12} className="text-[#ccff00]" /> / Asesores comerciales
            </p>
            {advisorsReport && (
              <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase" data-testid="admin-advisors-counter">
                {advisorsReport.advisors_count} asesor{advisorsReport.advisors_count !== 1 ? "es" : ""} · {advisorsReport.with_advisor}/{advisorsReport.total_stores} tiendas referidas
              </span>
            )}
          </div>
          {!advisorsReport || (advisorsReport.buckets || []).length === 0 ? (
            <div className="border border-white/10 rounded-2xl p-6 text-center text-white/40 text-sm">
              Sin datos de asesores todavía.
            </div>
          ) : (
            <div className="border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5">
              {advisorsReport.buckets.map((b) => {
                const key = b.advisor || "__none__";
                const expanded = expandedAdvisor === key;
                const hasAdvisor = !!b.advisor;
                return (
                  <div key={key} data-testid={`admin-advisor-${key}`}>
                    <button
                      onClick={() => setExpandedAdvisor(expanded ? null : key)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] text-left transition"
                    >
                      {expanded ? <ChevronDown size={14} className="text-white/40" /> : <ChevronRight size={14} className="text-white/40" />}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasAdvisor ? "bg-[#ccff00]/15 text-[#ccff00]" : "bg-white/5 text-white/40"}`}>
                        <UserCheck size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-display text-base tracking-tight ${hasAdvisor ? "" : "text-white/50 italic"}`}>
                          {b.advisor || "Sin asesor"}
                        </p>
                        <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">
                          {b.count} tienda{b.count !== 1 ? "s" : ""} · {b.active_count} activa{b.active_count !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${hasAdvisor ? "border-[#ccff00]/40 text-[#ccff00] bg-[#ccff00]/10" : "border-white/10 text-white/40"}`}>
                        {b.count}
                      </span>
                      {hasAdvisor && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(ev) => { ev.stopPropagation(); copyInvite(b.advisor); }}
                          onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); ev.stopPropagation(); copyInvite(b.advisor); } }}
                          className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-[#ccff00] transition cursor-pointer select-none"
                          title={`Copiar link de invitación de ${b.advisor}`}
                          data-testid={`admin-advisor-copy-${b.advisor}`}
                        >
                          <LinkIcon size={11} /> link
                        </span>
                      )}
                    </button>
                    {expanded && (
                      <div className="bg-black/40 border-t border-white/5">
                        <div className="grid grid-cols-[minmax(160px,2fr)_minmax(120px,1fr)_120px_140px_100px] px-4 py-2 bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50">
                          <span>Tienda</span><span>Dueño</span><span>Giro</span><span>Alta</span><span>Estatus</span>
                        </div>
                        {b.stores.map((s) => {
                          const meta = STATUS_META[s.subscription_status] || STATUS_META.trial;
                          return (
                            <div key={s.store_id} className="grid grid-cols-[minmax(160px,2fr)_minmax(120px,1fr)_120px_140px_100px] items-center px-4 py-2.5 border-t border-white/5 text-sm" data-testid={`admin-advisor-store-${s.store_id}`}>
                              <div className="truncate flex items-center gap-1"><Building2 size={11} className="text-white/40" /> {s.store_name}</div>
                              <div className="truncate text-white/70">{s.owner_name || "—"}</div>
                              <div className="text-white/60 text-xs truncate">{s.giro}</div>
                              <div className="text-white/60 text-xs">{fmtDate(s.created_at)}</div>
                              <div>
                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border ${meta.bg} ${meta.color}`}>
                                  {meta.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Comisiones por asesor */}
        <section id="comisiones" className="mb-10" data-testid="admin-commissions">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase flex items-center gap-2">
              <Wallet size={12} className="text-[#ccff00]" /> / Comisiones · $75 MXN por tienda activa
            </p>
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={commissionsMonth}
                onChange={(e) => setCommissionsMonth(e.target.value || commissionsMonth)}
                className="bg-black/60 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:border-[#ccff00]/50 focus:outline-none [color-scheme:dark]"
                data-testid="admin-commissions-month"
              />
              {commissionsReport && (
                <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase" data-testid="admin-commissions-summary">
                  {commissionsReport.total_stores} tiendas · <span className="text-[#ccff00]">{formatMXN(commissionsReport.total_commission_mxn)}</span> total
                </span>
              )}
            </div>
          </div>
          {!commissionsReport || (commissionsReport.buckets || []).length === 0 ? (
            <div className="border border-white/10 rounded-2xl p-6 text-center text-white/40 text-sm">
              Sin comisiones para {commissionsMonth}. Los asesores ganan {formatMXN(75)} MXN por cada tienda activa que refieran ese mes.
            </div>
          ) : (
            <div className="border border-white/10 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-[1fr_100px_140px_140px] px-4 py-2 bg-white/[0.04] text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">
                <span>Asesor</span>
                <span className="text-right">Tiendas</span>
                <span className="text-right">Comisión</span>
                <span className="text-right">Link</span>
              </div>
              {commissionsReport.buckets.map((b) => (
                <div key={b.advisor} className="grid grid-cols-[1fr_100px_140px_140px] items-center px-4 py-3 border-b border-white/5 last:border-none hover:bg-white/[0.02]" data-testid={`admin-commission-${b.advisor}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-[#ccff00]/15 text-[#ccff00] flex items-center justify-center flex-shrink-0">
                      <UserCheck size={12} />
                    </div>
                    <span className="font-display text-base tracking-tight truncate">{b.advisor}</span>
                  </div>
                  <div className="text-right font-mono text-sm text-white/70">{b.count}</div>
                  <div className="text-right font-satoshi text-lg text-[#ccff00]" data-testid={`admin-commission-amount-${b.advisor}`}>
                    {formatMXN(b.commission_mxn)}
                  </div>
                  <div className="text-right">
                    <button
                      onClick={() => copyInvite(b.advisor)}
                      className="inline-flex items-center gap-1 rounded-full border border-white/10 hover:border-[#ccff00]/40 hover:text-[#ccff00] text-white/60 px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition"
                      data-testid={`admin-commission-copy-${b.advisor}`}
                    >
                      <Copy size={11} /> Copiar
                    </button>
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_100px_140px_140px] items-center px-4 py-3 bg-black/40 border-t border-white/10">
                <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase">Total {commissionsMonth}</span>
                <span className="text-right font-mono text-sm text-white">{commissionsReport.total_stores}</span>
                <span className="text-right font-satoshi text-2xl text-[#ccff00]" data-testid="admin-commissions-total">
                  {formatMXN(commissionsReport.total_commission_mxn)}
                </span>
                <span />
              </div>
            </div>
          )}
        </section>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="flex-1 flex items-center gap-2 bg-black/60 border border-white/10 rounded-2xl px-3 py-2">
            <Search size={14} className="text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar tienda, dueño, celular o ID…"
              className="flex-1 bg-transparent outline-none text-sm placeholder-white/30"
              data-testid="admin-search"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { k: "", l: "Todas" },
              { k: "pending_payment", l: "Sin pagar" },
              { k: "active", l: "Activas" },
              { k: "expired", l: "Vencidas" },
              { k: "suspended", l: "Suspendidas" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setStatusFilter(f.k)}
                className={`px-3 py-2 rounded-full border text-[10px] font-mono uppercase tracking-widest transition-colors ${
                  statusFilter === f.k ? "border-white bg-white text-black" : "border-white/10 text-white/60 hover:text-white"
                }`}
                data-testid={`admin-filter-${f.k || "all"}`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-2xl overflow-hidden" data-testid="admin-stores-table">
          <div className="grid grid-cols-[minmax(180px,2fr)_minmax(120px,1fr)_120px_130px_120px_minmax(220px,1.5fr)] px-4 py-3 bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <span>Tienda</span>
            <span>Dueño / Cel.</span>
            <span>Estado</span>
            <span>Días</span>
            <span>Ventas</span>
            <span className="text-right pr-1">Acciones</span>
          </div>
          {loading ? (
            <div className="p-8 text-center text-white/40 text-sm">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-white/40 text-sm">Sin resultados.</div>
          ) : (
            filtered.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.trial;
              return (
                <div key={s.id} className="grid grid-cols-[minmax(180px,2fr)_minmax(120px,1fr)_120px_130px_120px_minmax(220px,1.5fr)] items-center px-4 py-3 border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-store-${s.id}`}>
                  <div className="min-w-0">
                    <p className="font-display text-base tracking-tight truncate">{s.name}</p>
                    <p className="font-mono text-[10px] text-white/40 truncate">{s.giro} · {s.id.slice(0, 8)} · <span className={s.plan === "onepay" ? "text-[#ccff00]" : "text-[#ff5a00]"} data-testid={`admin-plan-${s.id}`}>{s.plan === "onepay" ? "ONE PAY" : "CHALANITO"}</span></p>
                    <p className="font-mono text-[10px] text-white/30">Alta: {fmtDate(s.created_at)}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{s.owner_name || "—"}</p>
                    <p className="font-mono text-[11px] text-white/40 truncate">{s.phone || "sin celular"}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest w-fit ${meta.bg} ${meta.color}`}>
                    {meta.label}
                  </span>
                  <div className="text-sm">
                    {s.status === "active" ? (
                      <span className="text-emerald-400" data-testid={`admin-days-${s.id}`}>{s.days_left} días</span>
                    ) : s.status === "pending_payment" || s.status === "trial" ? (
                      <span className="text-[#ccff00]">Sin pagar</span>
                    ) : (
                      <span className="text-[#ff5a00]">Cobrar</span>
                    )}
                    <p className="font-mono text-[10px] text-white/40">{s.next_billing_at ? `hasta ${fmtDate(s.next_billing_at)}` : "sin periodo"}</p>
                  </div>
                  <div className="text-sm">
                    <p>{s.sales_count} ventas</p>
                    <p className="font-mono text-[10px] text-white/40">{s.users_count} usuarios · {s.products_count} prod.</p>
                  </div>
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    <ActionBtn testId={`admin-extend-${s.id}`} disabled={busyId === s.id} onClick={() => doAction(s.id, (id) => adminExtendTrial(id, 7), "Cortesía +7 días")} title="Regalar 7 días de cortesía"><Plus size={13} />7d</ActionBtn>
                    <ActionBtn testId={`admin-mark-paid-${s.id}`} disabled={busyId === s.id} onClick={() => doAction(s.id, adminMarkPaid, "Marcada como pagada")} title={`Marcar como pagada (${s.plan === "onepay" ? "365" : "30"} días)`} primary><DollarSign size={13} /></ActionBtn>
                    {s.status === "suspended" ? (
                      <ActionBtn testId={`admin-resume-${s.id}`} disabled={busyId === s.id} onClick={() => doAction(s.id, adminResume, "Tienda reactivada")} title="Reactivar"><Play size={13} /></ActionBtn>
                    ) : (
                      <ActionBtn testId={`admin-suspend-${s.id}`} disabled={busyId === s.id} onClick={() => doAction(s.id, adminSuspend, "Tienda suspendida")} title="Suspender"><Pause size={13} /></ActionBtn>
                    )}
                    <ActionBtn testId={`admin-delete-${s.id}`} disabled={busyId === s.id} onClick={() => {
                      if (window.confirm(`¿Eliminar definitivamente "${s.name}" y todos sus datos?`)) doAction(s.id, adminDeleteStore, "Tienda eliminada");
                    }} title="Eliminar" danger><Trash2 size={13} /></ActionBtn>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <FxRateCard />
        <p className="mt-6 text-center text-white/30 text-[10px] font-mono uppercase tracking-widest">
          CHALANITO $20 USD / mes (IA, 300 comandos/día) · ONE PAY $25 USD / año (escáner, sin IA) · Stripe o SPEI
        </p>
      </div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function StatCard({ icon, label, value, tint, tid, sub }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4" data-testid={tid}>
      <div className="flex items-center gap-1.5 text-white/40 text-[10px] font-mono uppercase tracking-widest">
        <span className={tint}>{icon}</span> {label}
      </div>
      <p className={`font-display text-3xl md:text-4xl tracking-tighter mt-2 leading-none ${tint}`}>{value}</p>
      {sub && <p className="mt-1 font-mono text-[10px] text-white/40 truncate">{sub}</p>}
    </div>
  );
}

function ActionBtn({ children, onClick, title, disabled, primary, danger, testId }) {
  const base = "inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider transition-colors disabled:opacity-50";
  const style = danger
    ? "border border-red-500/30 text-red-400 hover:bg-red-500/10"
    : primary
      ? "bg-emerald-500 text-black hover:bg-emerald-400"
      : "border border-white/10 text-white/70 hover:border-white/30 hover:text-white";
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled} title={title} className={`${base} ${style}`}>
      {children}
    </button>
  );
}
